import { Tab, Tabs, styled } from '@mui/material';
import { segmentedControl } from '../../theme/tokens/component';
import { state as semanticState } from '../../theme/tokens/semantic';

const { color, size, spacing, effects, state } = segmentedControl;
const { tab: tabColor, track } = color;

// divider style for the line between tabs. This is a custom implementation since MUI Tabs doesn't have a built in divider between tabs.
const dividerStyle = {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translateY(-50%)',
    height: '70%',
    width: 1,
    backgroundColor: track.border,
};

const selectedStyle = {
    color: tabColor.content.active,
    backgroundColor: tabColor.bg.active,
};

export const SegmentedControl = styled(Tabs, {
    shouldForwardProp: (prop) =>
        prop !== 'fullWidth' && prop !== 'size',
})(({ theme, fullWidth, size: sizeProp }) => ({
    maxWidth: 'unset',
    minHeight: size.tab.height[sizeProp],
    '& .MuiTabs-indicator': {
        backgroundColor: 'transparent',
    },
    '& .MuiTabs-flexContainer': {
        width: fullWidth ? '100%' : 'fit-content',
        padding: spacing.track.padding,
        backgroundColor: track.bg,
        border: `1px solid ${track.border}`,
        borderRadius: effects.radius.default,
    },
    '& .MuiTab-root': {
        position: 'relative',
        minHeight: size.tab.height[sizeProp],
        minWidth: size.tab.height[sizeProp],
        flex: fullWidth ? 1 : undefined,
        borderRadius: effects.radius.default,
        flexDirection: sizeProp === 'lg' ? 'column' : 'row',
        '&::before': dividerStyle,
        '&:first-of-type::before': { display: 'none' },
        '&.Mui-selected::before': { display: 'none' },
        '&.Mui-selected + .MuiTab-root::before': { display: 'none' },
        '& > .MuiTab-iconWrapper': {
            marginBottom: 0,
            height: size.icon[sizeProp],
            marginRight: 0
        },
    },
    [theme.breakpoints.down('md')]: {
        '& .MuiTabs-flexContainer': {
            width: fullWidth ? '100%' : 'fit-content',
        },
    },
    [theme.breakpoints.down('sm')]: {
        '& .MuiTab-root': {
            flexDirection: 'row',
            minHeight: size.tab.height.mobile,
        }
    },
}));

export const SegmentedTab = styled(
    ({ ...props }) => <Tab disableTouchRipple disableFocusRipple {...props} />
)(({ theme }) => ({
    maxWidth: 'unset',
    minWidth: 'unset',
    padding: `0 ${spacing.tab.padding.x}`,
    borderRadius: effects.radius.default,
    color: tabColor.content.inactive,
    textTransform: 'none',
    gap: theme.spacing('tiny'),
    alignItems: 'center',
    '&:hover': {
        color: tabColor.content.inactiveHover,
        opacity: 1,
    },
    '&.Mui-selected': selectedStyle,
    '&.Mui-focusVisible': {
        outline: `${semanticState.outline.weight.focus.md} solid ${semanticState.outline.color.focus}`,
        outlineOffset: semanticState.outline.offset.focus,
        borderRadius: semanticState.outline.radius.focus,
    },
    '&.Mui-disabled': {
        opacity: state.disabled,
    },
}));

const SegmentedControlGroup = ({ tabs, value, onChange, ...controlProps }) => (
    <SegmentedControl value={value} onChange={onChange} {...controlProps}>
        {tabs.map((tab) => (
            <SegmentedTab
                key={tab.label} icon={tab.icon} iconPosition={tab.iconPosition} label={tab.label} disabled={tab.disabled}
            />
        ))}
    </SegmentedControl>
);

export default SegmentedControlGroup;